import React, { useEffect, useRef, useMemo, useState } from 'react';
import './ToolSettings.css';
import { transformServerDataForApp, transformData2UI } from '../../config';
import { sendMessageToApp as sendAppCommand } from '../../utils/nativeBridge';

const NoiseReductionCard = ({
    strengthValue,
    strengthLabel,
    strFuction,
    color,
    label,
}) => {
    const cardClass = `noise-card card-${color}`;

    return (
        <div className={cardClass}>
            <div className={`ear-label ${color}`}>{label}</div>
            <div className="strength-label">{strengthLabel}</div>

            {strengthValue.length < 7 && (
                <div className="noise-reduction-text">
                    {strFuction}
                    <div className="strength-value">{strengthValue}</div>
                </div>
            )}
        </div>
    );
};

const sentCommandIds = new Set();
const recentAutoSendSignatures = new Map();
const AUTO_SEND_DEDUPE_WINDOW_MS = 1000;

const wasAutoSendRecentlyDispatched = (command) => {
    if (typeof command !== 'string' || command.length === 0) {
        return false;
    }

    const now = Date.now();

    for (const [signature, timestamp] of recentAutoSendSignatures.entries()) {
        if (now - timestamp > AUTO_SEND_DEDUPE_WINDOW_MS) {
            recentAutoSendSignatures.delete(signature);
        }
    }

    const lastDispatchedAt = recentAutoSendSignatures.get(command);
    if (lastDispatchedAt !== undefined && now - lastDispatchedAt < AUTO_SEND_DEDUPE_WINDOW_MS) {
        return true;
    }

    recentAutoSendSignatures.set(command, now);
    return false;
};

const ToolSettings = ({
    strategy = 1,
    strengthValue = "",
    leftOrRight = "A",
    isHistory = false,
    isLast = false,
    msgId
}) => {
    const commandSentRef = useRef(false);
    const [enableBt1, setEnableBt1] = useState(false);
    const [enableBt2, setEnableBt2] = useState(false);

    const { leftEarData, rightEarData, title, subtitle, commandSend } = useMemo(() => {
        if (strategy === undefined || strategy === '' || strategy === null) {
            return { leftEarData: null, rightEarData: null };
        }

        const data = { type: strategy, channel: leftOrRight, value: strengthValue };
        const cmd = transformServerDataForApp(data);
        const uiData = transformData2UI(data);
        const normalizedUiData = {
            ...uiData,
            step: uiData?.step === undefined || uiData?.step === null ? '' : String(uiData.step)
        };

        return {
            leftEarData: normalizedUiData,
            rightEarData: normalizedUiData,
            title: normalizedUiData.title,
            subtitle: normalizedUiData.subTitle,
            commandSend: cmd
        };
    }, [strategy, leftOrRight, strengthValue]);

    useEffect(() => {
        if (isHistory) return;

        if (isLast && commandSend && !sentCommandIds.has(msgId) && msgId !== undefined && !commandSentRef.current) {
            sentCommandIds.add(msgId);
            commandSentRef.current = true;

            if (wasAutoSendRecentlyDispatched(commandSend)) {
                return;
            }

            sendAppCommand(commandSend);
        }
    }, [commandSend, isHistory, isLast, msgId, strategy]);

    if (!leftEarData && !rightEarData) {
        return null;
    }

    const wrapperClass = `cards-wrapper ${
        leftEarData.channel === 3 ? 'dual-mode' : `single-mode ${leftEarData.channel === 1 ? 'left-ear' : 'right-ear'}`
    }`;

    const divLineClass = `${
        leftEarData.step.length < 7 ? 'divider-line' : 'divider-line-short'
    }`;

    const handleButtonApplyClick = () => {
        setEnableBt2(true);
        sendAppCommand(commandSend);
    };

    const handleButtonUndoClick = () => {
        setEnableBt1(true);
        let strategyTemp = strategy;

        if (strategy % 2 === 1) {
            strategyTemp += 1;
        } else {
            strategyTemp -= 1;
        }

        if (strategy < 17) {
            const data = { type: strategyTemp, channel: leftOrRight };
            const cmdTemp = transformServerDataForApp(data);
            sendAppCommand(cmdTemp);
        } else {
            const data = { type: strategy, channel: leftOrRight, value: strengthValue };
            const cmdTemp = transformServerDataForApp(data);
            const cmdTempJson = JSON.parse(cmdTemp);
            sendAppCommand("undo:" + cmdTempJson.type);
        }
    };

    return (
        <div className="settings-container">
            <div className="header">
                <h2 className="title">{title}</h2>
                <div className="subtitle">{subtitle}</div>
            </div>

            <div className={`${wrapperClass}`}>
                {(leftEarData.channel === 1 || leftEarData.channel === 3) && (
                    <NoiseReductionCard
                        strengthValue={leftEarData.step}
                        strengthLabel={leftEarData.stronger}
                        strFuction={leftEarData.function}
                        color="left"
                        label={leftEarData.label || "Left"}
                    />
                )}

                {(leftEarData.channel === 1 || leftEarData.channel === 3) &&
                (rightEarData.channel === 2 || rightEarData.channel === 3) && (
                    <div className={`${divLineClass}`} />
                )}

                {(rightEarData.channel === 2 || rightEarData.channel === 3) && (
                    <NoiseReductionCard
                        strengthValue={rightEarData.step}
                        strengthLabel={rightEarData.stronger}
                        strFuction={rightEarData.function}
                        color="right"
                        label={rightEarData.label || "Right"}
                    />
                )}
            </div>

            {leftEarData.step.length > 7 && (
                <div className="only-step">{leftEarData.step}</div>
            )}

            {!isHistory && isLast && strategy !== 0 && (
                <div className="action-buttons">
                    <button
                        className="action-button undo-button"
                        onClick={handleButtonUndoClick}
                        disabled={enableBt1}
                    >
                        {"Undo Change"}
                    </button>
                    <button
                        className="action-button apply-button"
                        onClick={handleButtonApplyClick}
                        disabled={enableBt2}
                    >
                        {"Apply Again"}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ToolSettings;
